import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

interface EventSessionActionMenuProps {
  sessionUuid: string;
  isCancelled?: boolean;
  onDeleteSession: (uuid: string) => void;
  onCancelSession?: (uuid: string) => void;
  onEditSession?: (uuid: string) => void;
  onDuplicateSession?: (uuid: string) => void;
}

const EventSessionActionMenu: React.FC<EventSessionActionMenuProps> = ({
  sessionUuid,
  isCancelled,
  onDeleteSession,
  onCancelSession,
  onEditSession,
  onDuplicateSession,
}) => {
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
          onClick={stopPropagation}
          size="icon"
          type="button"
          variant="ghost"
        >
          <span className="sr-only">More actions</span>
          <EllipsisVerticalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px] p-2 bg-white">
        {onEditSession && (
          <DropdownMenuItem
            className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => onEditSession(sessionUuid)}
          >
            <PencilSquareIcon className="mr-2 h-4 w-4 text-gray-500" />
            Edit
          </DropdownMenuItem>
        )}
        {onDuplicateSession && (
          <DropdownMenuItem
            className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => onDuplicateSession(sessionUuid)}
          >
            <DocumentDuplicateIcon className="mr-2 h-4 w-4 text-gray-500" />
            Duplicate
          </DropdownMenuItem>
        )}
        {onCancelSession && !isCancelled && (
          <DropdownMenuItem
            className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-orange-50 text-orange-600 transition-colors"
            onClick={() => onCancelSession(sessionUuid)}
          >
            <XCircleIcon className="mr-2 h-4 w-4" />
            Cancel
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="my-1 h-px bg-gray-200" />
        <DropdownMenuItem
          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-red-50 text-red-600 transition-colors"
          onClick={() => onDeleteSession(sessionUuid)}
        >
          <TrashIcon className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default EventSessionActionMenu;
